import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  createDailyPageActivity,
  getDailyPageDay,
  getDailyPageDayConfig,
  getDailyPageErrorMessage,
  getDailyPagePayload,
  isDailyPageApiSuccess,
  listDailyPageDays,
  putDailyPageDayConfig,
} from '../../services/dailyPageApi';
import {
  getDailyMissionsPayload,
  isDailyMissionsApiSuccess,
  listDailyMissions,
} from '../../services/dailyMissionsApi';
import {
  addDays,
  dayMaxFe,
  filterActivities,
  formatIstDate,
  istToday,
  monthBounds,
  parseDayConfig,
  publishStatus,
  CHALLENGE_TYPES,
  OPINION_TYPES,
} from './dailyTasksUtils';
import { parseDayList } from '../DailyMissions/dailyMissionUtils';

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function statusColor(status) {
  if (status === 'published') return 'success';
  if (status === 'locked') return 'warning';
  return 'default';
}

function DailyTasksCalendarPage() {
  const navigate = useNavigate();
  const today = istToday();
  const [cursor, setCursor] = useState(() => {
    const [y, m] = today.split('-').map(Number);
    return { year: y, month: m - 1 };
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [daysByDate, setDaysByDate] = useState({});
  const [missionDates, setMissionDates] = useState(new Set());
  const [configsByDate, setConfigsByDate] = useState({});
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState('');
  const [copyTarget, setCopyTarget] = useState('');
  const [copying, setCopying] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { start, end, lastDay } = useMemo(
    () => monthBounds(cursor.year, cursor.month),
    [cursor.year, cursor.month]
  );

  const loadMonth = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [daysRes, missionsRes] = await Promise.all([
        listDailyPageDays({ startDate: start, endDate: end }),
        listDailyMissions({ startDate: start, endDate: end, limit: 100 }),
      ]);

      if (!isDailyPageApiSuccess(daysRes)) {
        setError(getDailyPageErrorMessage(daysRes, 'Failed to load daily page days.'));
        setDaysByDate({});
      } else {
        const payload = getDailyPagePayload(daysRes) || {};
        const map = {};
        (payload.days || []).forEach((day) => {
          if (day?.activity_date) map[day.activity_date] = day;
        });
        setDaysByDate(map);
      }

      if (isDailyMissionsApiSuccess(missionsRes)) {
        const parsed = parseDayList(getDailyMissionsPayload(missionsRes));
        setMissionDates(new Set(parsed.occupiedDates || []));
      } else {
        setMissionDates(new Set());
      }

      // FE chips: load day-config for each day in the visible month
      const datesToLoad = [];
      for (let d = 1; d <= lastDay; d += 1) {
        datesToLoad.push(
          `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
        );
      }
      const configEntries = await Promise.all(
        datesToLoad.map(async (dayKey) => {
          const res = await getDailyPageDayConfig(dayKey);
          if (!isDailyPageApiSuccess(res)) return [dayKey, null];
          return [dayKey, parseDayConfig(getDailyPagePayload(res))];
        })
      );
      const configMap = {};
      configEntries.forEach(([dayKey, cfg]) => {
        if (cfg) configMap[dayKey] = cfg;
      });
      setConfigsByDate(configMap);
    } catch (err) {
      setError(err.message || 'Failed to load calendar.');
    } finally {
      setLoading(false);
    }
  }, [start, end, lastDay, cursor.year, cursor.month]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  const cells = useMemo(() => {
    const first = new Date(Date.UTC(cursor.year, cursor.month, 1));
    // Monday-first grid
    const jsDay = first.getUTCDay(); // 0 Sun
    const mondayOffset = (jsDay + 6) % 7;
    const total = mondayOffset + lastDay;
    const rows = Math.ceil(total / 7);
    const grid = [];
    for (let i = 0; i < rows * 7; i += 1) {
      const dayNum = i - mondayOffset + 1;
      if (dayNum < 1 || dayNum > lastDay) {
        grid.push(null);
      } else {
        grid.push(
          `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
        );
      }
    }
    return grid;
  }, [cursor.year, cursor.month, lastDay]);

  const monthLabel = new Date(Date.UTC(cursor.year, cursor.month, 1)).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  const handleCopy = async () => {
    if (!copySource || !copyTarget) return;
    setCopying(true);
    try {
      const [sourceDayRes, sourceConfigRes] = await Promise.all([
        getDailyPageDay(copySource),
        getDailyPageDayConfig(copySource),
      ]);
      if (!isDailyPageApiSuccess(sourceDayRes)) {
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(sourceDayRes, 'Failed to load source day.'),
          severity: 'error',
        });
        return;
      }
      const sourceDay = getDailyPagePayload(sourceDayRes) || {};
      const activities = Array.isArray(sourceDay.activities) ? sourceDay.activities : [];

      if (isDailyPageApiSuccess(sourceConfigRes)) {
        const cfg = parseDayConfig(getDailyPagePayload(sourceConfigRes));
        const putRes = await putDailyPageDayConfig(copyTarget, {
          missions_group_fe: cfg.missions_group_fe,
          challenge_group_fe: cfg.challenge_group_fe,
          opinions_group_fe: cfg.opinions_group_fe,
          completion_bonus_fe: cfg.completion_bonus_fe,
        });
        if (!isDailyPageApiSuccess(putRes)) {
          setSnackbar({
            open: true,
            message: getDailyPageErrorMessage(putRes, 'Failed to copy day-config.'),
            severity: 'error',
          });
          return;
        }
      }

      for (const activity of activities) {
        if (!activity?.is_active && activity?.is_active !== undefined) continue;
        const body = {
          activity_type: activity.activity_type,
          title: activity.title,
          subtitle: activity.subtitle || null,
          image_url: activity.image_url || null,
          sort_order: activity.sort_order ?? 1,
          completion_mode: activity.completion_mode || 'participation',
          target_value: activity.target_value ?? 1,
          action_key: activity.action_key || null,
          navigation_link: activity.navigation_link || null,
          ref_type: activity.ref_type || null,
          ref_id: activity.ref_id || null,
          fan_energy_reward: 0,
          config: activity.config || {},
        };
        const createRes = await createDailyPageActivity(copyTarget, body);
        if (!isDailyPageApiSuccess(createRes)) {
          setSnackbar({
            open: true,
            message: getDailyPageErrorMessage(createRes, 'Failed while copying an activity.'),
            severity: 'error',
          });
          return;
        }
      }

      setCopyOpen(false);
      setSnackbar({
        open: true,
        message: `Copied activities + rewards from ${copySource} → ${copyTarget}. Mission pack is not copied — edit it separately.`,
        severity: 'success',
      });
      await loadMonth();
      navigate(`/admin/daily-tasks/day/${copyTarget}`);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Copy failed.', severity: 'error' });
    } finally {
      setCopying(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={() =>
              setCursor((c) => {
                const m = c.month - 1;
                return m < 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: m };
              })
            }
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 180, textAlign: 'center' }}>
            {monthLabel}
          </Typography>
          <IconButton
            onClick={() =>
              setCursor((c) => {
                const m = c.month + 1;
                return m > 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: m };
              })
            }
          >
            <ChevronRightIcon />
          </IconButton>
          <Chip size="small" label="IST calendar days" variant="outlined" />
        </Box>
        <Box display="flex" gap={1}>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              setCopySource(addDays(today, -1));
              setCopyTarget(addDays(today, 1));
              setCopyOpen(true);
            }}
          >
            Copy from date
          </Button>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadMonth} disabled={loading}>
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            component={Link}
            to={`/admin/daily-tasks/day/${addDays(today, 1)}`}
          >
            Open tomorrow
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 1,
              mb: 1,
            }}
          >
            {WEEKDAY_HEADERS.map((d) => (
              <Typography key={d} variant="caption" color="text.secondary" fontWeight={600}>
                {d}
              </Typography>
            ))}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 1,
            }}
          >
            {cells.map((date, idx) => {
              if (!date) {
                return (
                  <Paper
                    key={`empty-${idx}`}
                    variant="outlined"
                    sx={{ p: 1, minHeight: 120, bgcolor: '#fafafa' }}
                  />
                );
              }
              const day = daysByDate[date] || {};
              const activities = day.activities || [];
              const challenges = filterActivities(activities, CHALLENGE_TYPES).filter(
                (a) => a.is_active !== false
              );
              const opinions = filterActivities(activities, OPINION_TYPES).filter(
                (a) => a.is_active !== false
              );
              const hasMission = missionDates.has(date);
              const status = publishStatus({
                is_published: day.is_published,
                locked: day.locked,
              });
              const config = configsByDate[date];
              const totalFe = config ? dayMaxFe(config) : null;
              const isToday = date === today;

              return (
                <Paper
                  key={date}
                  variant="outlined"
                  sx={{
                    p: 1,
                    minHeight: 120,
                    borderColor: isToday ? 'primary.main' : 'divider',
                    borderWidth: isToday ? 2 : 1,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2 },
                  }}
                  onClick={() => navigate(`/admin/daily-tasks/day/${date}`)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {Number(date.slice(-2))}
                    </Typography>
                    <Chip
                      size="small"
                      label={status}
                      color={statusColor(status)}
                      sx={{ height: 20, fontSize: 10 }}
                    />
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    <Chip
                      size="small"
                      label={hasMission ? 'Missions ready' : 'Missions missing'}
                      color={hasMission ? 'success' : 'default'}
                      variant={hasMission ? 'filled' : 'outlined'}
                      sx={{ height: 20, fontSize: 10 }}
                    />
                    <Chip
                      size="small"
                      label={`Challenges ${challenges.length}`}
                      sx={{ height: 20, fontSize: 10 }}
                    />
                    <Chip
                      size="small"
                      label={`Opinions ${opinions.length}`}
                      sx={{ height: 20, fontSize: 10 }}
                    />
                    {totalFe != null && (
                      <Chip
                        size="small"
                        label={`${totalFe} FE`}
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </>
      )}

      <Dialog open={copyOpen} onClose={() => !copying && setCopyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy day (client-side)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copies day-config rewards and activities from the source IST date onto the target.
            Mission packs are not copied — use the Daily Missions editor for that.
          </Typography>
          <TextField
            fullWidth
            label="Source date"
            type="date"
            value={copySource}
            onChange={(e) => setCopySource(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={formatIstDate(copySource)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Target date"
            type="date"
            value={copyTarget}
            onChange={(e) => setCopyTarget(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={formatIstDate(copyTarget)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)} disabled={copying}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCopy} disabled={copying || !copySource || !copyTarget}>
            {copying ? 'Copying…' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DailyTasksCalendarPage;
